<template>
  <Teleport to="#appContent">
    <div
      class="fixed"
      v-if="type !== 'ui-less'"
      :style="{ zIndex: zIndex + ' !important' }"
      :class="
        typeof mainClass == 'function'
          ? mainClass({
              dialogVisible: dialogVisibleFinalValue,
              zIndex,
              dialogId,
              isLastDialog: isLastDialog
            })
          : mainClass
      "
    >
      <Transition
        name="fade"
        appear
        v-if="type !== 'ui-less' && type !== 'fullscreen' && !noOverlay"
      >
        <div
          v-if="dialogVisibleFinalValue"
          class="fixed inset-0 bg-black/30 z-50"
          @click="internalClose('overlay')"
        ></div>
      </Transition>
      <Transition
        :name="
          type === 'bottomsheet'
            ? 'bottomsheet'
            : type === 'fullscreen'
            ? 'modal-fullscreen'
            : 'modal'
        "
        appear
        v-if="type !== 'ui-less'"
        @before-enter="onOpening"
        @after-enter="onOpened"
        @before-leave="onClosing"
        @after-leave="onClosed"
      >
        <div
          v-if="dialogVisibleFinalValue"
          :class="{
            'fixed inset-0 flex items-center justify-center p-4 z-50 modalContainer':
              type === 'modal',
            'fixed inset-0  z-50 pt-[15vh] overflow-auto flex justify-center bottomsheetContainer bg-[linear-gradient(0deg,#fff_0%,#fff_30%,#fff0_31%)]':
              type === 'bottomsheet',
            'fixed inset-0  z-50 bg-white fullscreenDialogContainer':
              type === 'fullscreen',
            '[&_.mobile-status-padding]:pt-12 [&_.mobile-bottom-padding]:pt-8 [&_.mobile-status-padding-mt]:mt-12':
              $stores.shared?.state?.isAndroidApp
          }"
        >
          <div
            class="w-full h-full fixed top-0 left-0"
            @click="internalClose('closeButton')"
            v-if="type == 'modal' || type == 'bottomsheet'"
          ></div>
          <div
            :class="{
              'bg-white rounded-2xl shadow-xl max-w-md md:max-w-3xl w-full max-h-[90vh] overflow-auto relative modalBox':
                type === 'modal',
              'bg-white rounded-t-2xl shadow-xl md:max-w-3xl w-full relative mt-auto bottomsheetBox' :
                type === 'bottomsheet',
              'bg-white h-[100svh] w-full fullscreenDialogBox overflow-auto': type == 'fullscreen'
            }"
          >
            <div
              v-if="!hideCloseButton && type !== 'ui-less'"
              class="absolute top-3 right-3 cursor-pointer z-10"
              :class="{ 'mobile-status-padding-mt': type == 'fullscreen' }"
              @click="internalClose('closeButton')"
            >
              <slot name="closeIcon">
                <div class="w-8 h-8 bg-white rounded-full">
                  <ColorImg
                    class="w-full h-full bg-black"
                    :src="closeSvg"
                    alt=""
                  />
                </div>
              </slot>
            </div>

            <div
              class="w-full"
              :class="{
                'p-6': !noInnerPadding,
                'h-full': type !== 'bottomsheet'
              }"
            >
              <slot
                :dialogId="dialogId"
                :isLastDialog="isLastDialog"
                :zIndex="zIndex"
                :dialogVisible="dialogVisibleFinalValue"
                :transitionStatus="transitionStatus"
              ></slot>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <slot
      v-if="type === 'ui-less'"
      :dialogVisible="dialogVisibleFinalValue"
      :zIndex="zIndex"
      :dialogId="dialogId"
      :isLastDialog="isLastDialog"
      :transitionStatus="transitionStatus"
    ></slot>

    <VBusHelper
      :reactOn="$constants.EVENTS.DIALOG.BACK_ACTION_CLOSE_REQUIRED"
      :handler="onBackCloseRequested"
    />
  </Teleport>
</template>

<script setup>
import Commons from '../../features/shared/libraries/commons';
import closeSvg from './close.svg';
import { computed, getCurrentInstance, ref, watch } from 'vue';

const { proxy } = getCurrentInstance();
const dialogIndex = ref(0);

const props = defineProps({
  mainClass: { type: [String, Function], default: null },
  dialogVisible: { type: Boolean, default: null },
  dialogId: { type: String, default: () => `dialog-${Commons.generateUUID()}` },
  type: {
    type: String,
    default: 'modal' // 'modal' | 'bottomsheet' | 'fullscreen' | 'ui-less'
  },
  hideCloseButton: { type: Boolean, default: false },
  noInnerPadding: { type: Boolean, default: false },
  noOverlay: { type: Boolean, default: false }
});
const emit = defineEmits(['closed']);

const internalVisible = ref(false);
const transitionStatus = ref(null); // 'opening' | 'closing' | null

const dialogVisibleFinalValue = computed(() => {
  if (typeof props.dialogVisible === 'boolean') return props.dialogVisible;
  return internalVisible.value;
});

const zIndex = computed(() => 50 + dialogIndex.value);

const isLastDialog = computed(() => {
  return (
    props.dialogId === proxy.$stores.dialog.lastOpenedDialog?.id ||
    dialogIndex.value === proxy.$stores.dialog.state.lastDialogIndex
  );
});

const internalClose = (source) => {
  emit('closed', { source });
  internalVisible.value = false;
  transitionStatus.value = 'closing';
};

function show() {
  internalVisible.value = true;
  transitionStatus.value = 'opening';
}
function hide(source) {
  internalClose(source || 'external');
}
function onBackCloseRequested({ index }) {
  if (index === dialogIndex.value) internalClose('backClick');
}

// Watch dialogVisible pour mettre à jour l'index et émettre events
watch(
  () => dialogVisibleFinalValue.value,
  (newVal) => {
    let event;
    if (newVal) {
      dialogIndex.value = Number(++proxy.$stores.dialog.state.lastDialogIndex);
      event = proxy.$constants.EVENTS.DIALOG.OPENED;
    } else {
      event = proxy.$constants.EVENTS.DIALOG.CLOSED;
    }
    proxy.$bus.emit(event, { index: dialogIndex.value, id: props.dialogId });
  }
);

// Functions pour Transition
function onOpening() {
  transitionStatus.value = 'opening';
}

function onOpened() {
  setTimeout(() => {
    transitionStatus.value = null; // transition ouverte terminée
  }, 500);
}

function onClosing() {
  transitionStatus.value = 'closing';
}

function onClosed() {
  transitionStatus.value = null; // transition fermée terminée
}

defineExpose({ show, hide, open: show, close: hide, transitionStatus });
</script>
